// frontend/src/components/ExpenseCreateAccordion.jsx
// Project:      Vision Systems by Castle Traffic Ltd
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-09-17
// Updated:      2026-09-08 10:34 BST
//
// Purpose:
//   Shared "Add Expense" accordion for JobCreateFlyout / JobFormWidget
//   (deferred queue mode) and Edit Job Modal (immediate create mode).
//
// Existing Functional Contract:
//   - Enforces resource_id in the UI because the backend requires a non-null
//     resource association.
//   - Immediate mode (`jobId` supplied) POSTs the expense directly and can then
//     attach an optional receipt to /api/expenses/{id}/receipt.
//   - Deferred mode (`jobId` absent) emits expense drafts to the parent through
//     onDraftsChange for creation after the job exists.
//   - Loads expense categories from /api/expenses/categories.
//   - Preserves the forwardRef API: getDrafts() and clearDrafts().
//
// Mobile Responsiveness (2026-09-08):
//   - Preserves the existing desktop two-column form layout.
//   - Collapses every form row to one column below 768px.
//   - Makes form fields, accordion controls and actions touch-safe.
//   - Stacks status text and action buttons cleanly on phones.
//   - Converts the queued-expense table into labelled cards below 768px using
//     the same drafts/table DOM path.
//   - Measures every queued mobile card and sizes the internal viewport to the
//     tallest record so at least one complete expense is always visible.
//   - Adds mandatory vertical snap between complete queued expense records.
//   - Allows descriptions/status messages/filenames to wrap safely.
//
// Operational Notes:
//   - Receipt upload remains an immediate-mode operation because the receipt
//     endpoint requires a persisted expense ID. Deferred receipt persistence is
//     outside this component's current contract.
//   - Tenant context is not supplied by this component.
//   - Parent widgets/flyouts own their outer layout and ReactGridLayout sizing.

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import axiosInstance from "../api/axiosInstance";

const MOBILE_BREAKPOINT = 768;
const MOBILE_QUEUE_FALLBACK_HEIGHT = 360;

const sectionTitle = {
  fontSize: "1.05rem",
  fontWeight: 700,
  margin: "8px 0 6px",
};

const fieldLabel = {
  fontSize: "0.8rem",
  color: "#333",
  marginBottom: 4,
};

const desktopInputBase = {
  width: "100%",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  padding: "8px 10px",
  fontFamily: "var(--font-body)",
  fontSize: "0.9rem",
  outline: "none",
  boxSizing: "border-box",
};

const btn = (bg, color = "#fff") => ({
  background: bg,
  color,
  border: "none",
  borderRadius: 6,
  padding: "8px 12px",
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "var(--font-body)",
});

const defaultForm = (resourceId) => ({
  occurred_at: "",
  amount: "",
  currency: "GBP",
  category: "",
  description: "",
  status: "submitted",
  resource_id: resourceId || "",
});

const formatQueuedDate = (value) => {
  if (!value) return "—";

  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-GB");
  } catch {
    return "—";
  }
};

const ExpenseCreateAccordion = forwardRef(function ExpenseCreateAccordion(
  {
    jobId = null,
    customerId = null,
    defaultResourceId = "",
    onDraftsChange = () => {},
    title = "Expenses (create)",
    accent = "var(--accent-color, #0e2a3f)",
  },
  ref
) {
  const queuedRowRefs = useRef(new Map());

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() =>
    defaultForm(defaultResourceId)
  );
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const [categories, setCategories] = useState([]);

  const [drafts, setDrafts] = useState([]);

  const [receiptFile, setReceiptFile] = useState(null);

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.innerWidth < MOBILE_BREAKPOINT
      : false
  );

  const [mobileQueueHeight, setMobileQueueHeight] = useState(null);

  useEffect(() => {
    const updateViewportMode = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    updateViewportMode();
    window.addEventListener("resize", updateViewportMode);

    return () => {
      window.removeEventListener("resize", updateViewportMode);
    };
  }, []);

  useEffect(() => {
    let active = true;

    axiosInstance
      .get("/expenses/categories", { baseURL: "/api" })
      .then((response) => {
        if (!active) return;
        setCategories(
          Array.isArray(response.data) ? response.data : []
        );
      })
      .catch(() => {
        if (active) setCategories([]);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    onDraftsChange(drafts);
  }, [drafts, onDraftsChange]);

  useEffect(() => {
    setForm((current) => ({
      ...current,
      resource_id: defaultResourceId || "",
    }));
  }, [defaultResourceId]);

  useImperativeHandle(ref, () => ({
    getDrafts: () => drafts,
    clearDrafts: () => setDrafts([]),
  }));

  const resetForm = () => {
    setForm(defaultForm(defaultResourceId));
    setReceiptFile(null);
  };

  const toISODate = (value) =>
    value ? new Date(value).toISOString() : null;

  const doUploadReceipt = async (expenseId) => {
    if (!receiptFile) {
      return { ok: true };
    }

    const formData = new FormData();
    formData.append(
      "file",
      receiptFile,
      receiptFile.name || "receipt"
    );

    try {
      await axiosInstance.post(
        `/expenses/${expenseId}/receipt`,
        formData,
        {
          baseURL: "/api",
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        error:
          error?.response?.data?.detail ||
          error?.message ||
          "Failed to upload receipt.",
      };
    }
  };

  const addExpense = async () => {
    setInfo(null);

    const iso = toISODate(form.occurred_at);
    const amount = Number(form.amount);

    if (!iso || !Number.isFinite(amount)) {
      setInfo({
        type: "error",
        text: "Please provide a valid date and numeric amount.",
      });
      return;
    }

    const resolvedResourceId =
      form.resource_id || defaultResourceId;

    if (!resolvedResourceId) {
      setInfo({
        type: "error",
        text:
          "Resource is required. Please enter a Resource ID (or set a default).",
      });
      return;
    }

    const payload = {
      occurred_at: iso,
      amount,
      currency: form.currency || "GBP",
      category: form.category || null,
      description: form.description || null,
      status: form.status || "submitted",
      customer_id: customerId || null,
      resource_id: Number(resolvedResourceId),
      ...(jobId ? { job_id: Number(jobId) } : {}),
    };

    if (jobId) {
      setBusy(true);

      try {
        const response = await axiosInstance.post(
          "/expenses",
          payload,
          {
            baseURL: "/api",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const created = response?.data;

        if (!created?.id) {
          setInfo({
            type: "error",
            text: "Expense created but no ID returned.",
          });
          return;
        }

        const receiptResult = await doUploadReceipt(created.id);

        if (!receiptResult.ok) {
          setInfo({
            type: "error",
            text:
              `Expense saved, but receipt upload failed: ` +
              receiptResult.error,
          });

          window.dispatchEvent(
            new CustomEvent("dashboard:refresh")
          );
          return;
        }

        setInfo({
          type: "ok",
          text:
            "Expense created." +
            (receiptFile ? " Receipt attached." : ""),
        });

        resetForm();

        window.dispatchEvent(
          new CustomEvent("dashboard:refresh")
        );
      } catch (error) {
        const message =
          error?.response?.data?.detail ||
          error?.message ||
          "Failed to create expense.";

        setInfo({
          type: "error",
          text: String(message),
        });
      } finally {
        setBusy(false);
      }
    } else {
      setDrafts((current) => [...current, payload]);

      setInfo({
        type: "ok",
        text: "Added to job creation queue.",
      });

      resetForm();
    }
  };

  const removeDraft = (index) => {
    setDrafts((current) =>
      current.filter((_, itemIndex) => itemIndex !== index)
    );
  };

  const queuedTotal = useMemo(
    () =>
      drafts.reduce(
        (sum, draft) =>
          Number.isFinite(draft.amount)
            ? sum + draft.amount
            : sum,
        0
      ),
    [drafts]
  );

  useEffect(() => {
    queuedRowRefs.current.forEach((_, key) => {
      const index = Number(
        String(key).replace(/^draft-/, "")
      );

      if (
        !Number.isInteger(index) ||
        index < 0 ||
        index >= drafts.length
      ) {
        queuedRowRefs.current.delete(key);
      }
    });
  }, [drafts]);

  useEffect(() => {
    if (!isMobile || !open || drafts.length === 0) {
      setMobileQueueHeight(null);
      return undefined;
    }

    let frameId = null;
    let observer = null;

    const measureRows = () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        const heights = Array.from(
          queuedRowRefs.current.values()
        )
          .filter(Boolean)
          .map((node) =>
            Math.ceil(node.getBoundingClientRect().height)
          )
          .filter((height) => height > 0);

        if (heights.length) {
          setMobileQueueHeight(Math.max(...heights));
        }
      });
    };

    measureRows();
    window.addEventListener("resize", measureRows);

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measureRows);

      queuedRowRefs.current.forEach((node) => {
        if (node) observer.observe(node);
      });
    }

    return () => {
      window.removeEventListener("resize", measureRows);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [isMobile, open, drafts]);

  const inputBase = useMemo(
    () => ({
      ...desktopInputBase,
      minHeight: isMobile ? 44 : undefined,
      fontSize: isMobile ? 16 : desktopInputBase.fontSize,
    }),
    [isMobile]
  );

  const rowStyle = useMemo(
    () => ({
      display: "grid",
      gridTemplateColumns: isMobile
        ? "minmax(0, 1fr)"
        : "minmax(0, 1fr) minmax(0, 1fr)",
      gap: 12,
    }),
    [isMobile]
  );

  const mobileCellStyle = isMobile
    ? {
        display: "grid",
        gridTemplateColumns: "92px minmax(0, 1fr)",
        gap: 10,
        alignItems: "start",
        padding: "8px 10px",
        borderBottom: "1px solid #e5e7eb",
        overflowWrap: "anywhere",
      }
    : undefined;

  const renderMobileLabel = (label) =>
    isMobile ? (
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "#6b7280",
        }}
      >
        {label}
      </span>
    ) : null;

  const queueViewportHeight =
    mobileQueueHeight || MOBILE_QUEUE_FALLBACK_HEIGHT;

  const infoColor =
    info?.type === "error"
      ? "#b91c1c"
      : info?.type === "ok"
      ? "#065f46"
      : "#374151";

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        overflow: "hidden",
        marginTop: 12,
        minWidth: 0,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        style={{
          width: "100%",
          minHeight: isMobile ? 48 : undefined,
          textAlign: "left",
          background: open ? accent : "#f3f4f6",
          color: open ? "#fff" : "#111",
          padding: "10px 12px",
          fontWeight: 700,
          border: "none",
          cursor: "pointer",
          fontFamily: "var(--font-body)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <span
          style={{
            minWidth: 0,
            overflowWrap: "anywhere",
          }}
        >
          {title}

          {!jobId && drafts.length > 0 && (
            <span
              style={{
                marginLeft: 8,
                fontWeight: 600,
              }}
            >
              • {drafts.length} queued (£
              {queuedTotal.toFixed(2)})
            </span>
          )}
        </span>

        <span
          aria-hidden="true"
          style={{
            flexShrink: 0,
            fontSize: 16,
          }}
        >
          {open ? "▾" : "▸"}
        </span>
      </button>

      {open && (
        <div
          style={{
            padding: isMobile ? 12 : 12,
            background: "#fff",
            minWidth: 0,
          }}
        >
          <div style={rowStyle}>
            <div style={{ minWidth: 0 }}>
              <div style={fieldLabel}>Occurred</div>

              <input
                type="date"
                style={inputBase}
                value={form.occurred_at}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    occurred_at: event.target.value,
                  }))
                }
              />
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={fieldLabel}>Amount</div>

              <input
                type="number"
                step="0.01"
                inputMode="decimal"
                style={inputBase}
                value={form.amount}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    amount: event.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div
            style={{
              ...rowStyle,
              marginTop: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={fieldLabel}>Currency</div>

              <select
                style={inputBase}
                value={form.currency}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    currency: event.target.value,
                  }))
                }
              >
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={fieldLabel}>Category</div>

              <input
                list="expense-categories"
                style={inputBase}
                placeholder="e.g., Meals, Lodging"
                value={form.category}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    category: event.target.value,
                  }))
                }
              />

              <datalist id="expense-categories">
                {categories.map((category) => (
                  <option
                    key={category}
                    value={category}
                  />
                ))}
              </datalist>
            </div>
          </div>

          <div
            style={{
              ...rowStyle,
              marginTop: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={fieldLabel}>Status</div>

              <select
                style={inputBase}
                value={form.status}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    status: event.target.value,
                  }))
                }
              >
                <option value="submitted">
                  Submitted
                </option>
                <option value="draft">Draft</option>
                <option value="approved">
                  Approved
                </option>
                <option value="rejected">
                  Rejected
                </option>
                <option value="paid">Paid</option>
              </select>
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={fieldLabel}>
                Resource (required)
              </div>

              <input
                type="number"
                inputMode="numeric"
                min="1"
                style={inputBase}
                placeholder="Resource ID"
                value={form.resource_id}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    resource_id: event.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={fieldLabel}>Description</div>

            <textarea
              style={{
                ...inputBase,
                minHeight: isMobile ? 110 : 80,
                resize: "vertical",
              }}
              placeholder="Add a note (optional)…"
              value={form.description}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={fieldLabel}>
              Attach receipt (optional)
            </div>

            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(event) =>
                setReceiptFile(
                  event.target.files?.[0] || null
                )
              }
              style={{
                ...inputBase,
                padding: isMobile ? "8px 10px" : inputBase.padding,
              }}
            />

            {receiptFile && (
              <div
                style={{
                  fontSize: 12,
                  color: "#374151",
                  marginTop: 6,
                  overflowWrap: "anywhere",
                }}
              >
                Selected:{" "}
                <strong>{receiptFile.name}</strong> (
                {receiptFile.type || "binary"},{" "}
                {receiptFile.size} bytes)
              </div>
            )}

            {!jobId && receiptFile && (
              <div
                style={{
                  fontSize: 12,
                  color: "#92400e",
                  marginTop: 6,
                  overflowWrap: "anywhere",
                }}
              >
                Receipt upload occurs only after an expense
                has a persisted ID. This deferred draft does
                not currently queue receipt bytes.
              </div>
            )}
          </div>

          <div
            style={{
              display: isMobile ? "grid" : "flex",
              gridTemplateColumns: isMobile
                ? "minmax(0, 1fr)"
                : undefined,
              alignItems: isMobile ? "stretch" : "center",
              justifyContent: "space-between",
              gap: isMobile ? 10 : 12,
              marginTop: 12,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: infoColor,
                overflowWrap: "anywhere",
                minWidth: 0,
              }}
            >
              {info?.text || ""}
            </div>

            <div
              style={{
                display: isMobile ? "grid" : "flex",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : undefined,
                gap: 8,
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                onClick={resetForm}
                style={{
                  ...btn("#e5e7eb", "#111"),
                  minHeight: isMobile ? 44 : undefined,
                  width: isMobile ? "100%" : undefined,
                }}
              >
                Clear
              </button>

              <button
                type="button"
                onClick={addExpense}
                disabled={busy}
                style={{
                  ...btn("#ff7a00"),
                  minHeight: isMobile ? 44 : undefined,
                  width: isMobile ? "100%" : undefined,
                  opacity: busy ? 0.6 : 1,
                  cursor: busy ? "not-allowed" : "pointer",
                }}
              >
                {busy
                  ? "Saving…"
                  : jobId
                  ? "Add Expense"
                  : "Queue Expense"}
              </button>
            </div>
          </div>

          {!jobId && drafts.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={sectionTitle}>Queued</div>

              <div
                style={
                  isMobile
                    ? {
                        height: `${queueViewportHeight}px`,
                        minHeight: `${queueViewportHeight}px`,
                        maxHeight: `${queueViewportHeight}px`,
                        overflowY: "auto",
                        overflowX: "hidden",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        scrollSnapType: "y mandatory",
                        overscrollBehavior: "contain",
                        WebkitOverflowScrolling: "touch",
                      }
                    : {
                        width: "100%",
                        overflowX: "auto",
                      }
                }
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "0.92rem",
                    ...(isMobile
                      ? {
                          display: "block",
                        }
                      : {}),
                  }}
                >
                  <thead
                    style={
                      isMobile
                        ? { display: "none" }
                        : undefined
                    }
                  >
                    <tr style={{ background: "#f3f4f6" }}>
                      <th
                        style={{
                          textAlign: "left",
                          padding: 8,
                        }}
                      >
                        Occurred
                      </th>
                      <th
                        style={{
                          textAlign: "right",
                          padding: 8,
                        }}
                      >
                        Amount
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: 8,
                        }}
                      >
                        Currency
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: 8,
                        }}
                      >
                        Category
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: 8,
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: 8,
                        }}
                      >
                        Resource
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: 8,
                        }}
                      >
                        Description
                      </th>
                      <th
                        style={{
                          textAlign: "center",
                          padding: 8,
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    style={
                      isMobile
                        ? {
                            display: "block",
                            width: "100%",
                          }
                        : undefined
                    }
                  >
                    {drafts.map((draft, index) => {
                      const rowKey = `draft-${index}`;

                      return (
                        <tr
                          key={rowKey}
                          ref={(node) => {
                            if (node) {
                              queuedRowRefs.current.set(
                                rowKey,
                                node
                              );
                            } else {
                              queuedRowRefs.current.delete(
                                rowKey
                              );
                            }
                          }}
                          style={
                            isMobile
                              ? {
                                  display: "block",
                                  width: "100%",
                                  borderTop:
                                    index === 0
                                      ? "none"
                                      : "1px solid #eee",
                                  background: "#fff",
                                  scrollSnapAlign: "start",
                                  scrollSnapStop: "always",
                                }
                              : {
                                  borderTop:
                                    "1px solid #eee",
                                }
                          }
                        >
                          <td
                            style={
                              isMobile
                                ? mobileCellStyle
                                : { padding: 8 }
                            }
                          >
                            {renderMobileLabel("Occurred")}
                            <span>
                              {formatQueuedDate(
                                draft.occurred_at
                              )}
                            </span>
                          </td>

                          <td
                            style={
                              isMobile
                                ? mobileCellStyle
                                : {
                                    padding: 8,
                                    textAlign: "right",
                                  }
                            }
                          >
                            {renderMobileLabel("Amount")}
                            <span>
                              {Number(
                                draft.amount
                              ).toFixed(2)}
                            </span>
                          </td>

                          <td
                            style={
                              isMobile
                                ? mobileCellStyle
                                : { padding: 8 }
                            }
                          >
                            {renderMobileLabel("Currency")}
                            <span>
                              {draft.currency}
                            </span>
                          </td>

                          <td
                            style={
                              isMobile
                                ? mobileCellStyle
                                : { padding: 8 }
                            }
                          >
                            {renderMobileLabel("Category")}
                            <span
                              style={{
                                overflowWrap: "anywhere",
                              }}
                            >
                              {draft.category || "—"}
                            </span>
                          </td>

                          <td
                            style={
                              isMobile
                                ? mobileCellStyle
                                : { padding: 8 }
                            }
                          >
                            {renderMobileLabel("Status")}
                            <span>
                              {draft.status}
                            </span>
                          </td>

                          <td
                            style={
                              isMobile
                                ? mobileCellStyle
                                : { padding: 8 }
                            }
                          >
                            {renderMobileLabel("Resource")}
                            <span>
                              {draft.resource_id ?? "—"}
                            </span>
                          </td>

                          <td
                            style={
                              isMobile
                                ? mobileCellStyle
                                : {
                                    padding: 8,
                                    maxWidth: 260,
                                    overflow: "hidden",
                                    textOverflow:
                                      "ellipsis",
                                    whiteSpace:
                                      "nowrap",
                                  }
                            }
                          >
                            {renderMobileLabel(
                              "Description"
                            )}
                            <span
                              style={
                                isMobile
                                  ? {
                                      whiteSpace:
                                        "normal",
                                      overflowWrap:
                                        "anywhere",
                                    }
                                  : undefined
                              }
                            >
                              {draft.description || "—"}
                            </span>
                          </td>

                          <td
                            style={
                              isMobile
                                ? {
                                    ...mobileCellStyle,
                                    borderBottom: "none",
                                  }
                                : {
                                    padding: 8,
                                    textAlign: "center",
                                  }
                            }
                          >
                            {renderMobileLabel("Actions")}

                            <button
                              type="button"
                              onClick={() =>
                                removeDraft(index)
                              }
                              style={{
                                ...btn("#ef4444"),
                                minHeight: isMobile
                                  ? 44
                                  : undefined,
                                width: isMobile
                                  ? "100%"
                                  : undefined,
                              }}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div
                style={{
                  marginTop: 8,
                  fontWeight: 700,
                }}
              >
                Total £{queuedTotal.toFixed(2)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default ExpenseCreateAccordion;
